import React from "react";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import { colors } from "../../config";

interface PriceInputProps {
  id: string;
  label: string;
  value: number | string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  percentChange?: number | null;
  onInfoIconClick?: (event: React.MouseEvent<SVGSVGElement>) => void;
}

export default function PriceInput({
  id,
  label,
  value,
  onChange,
  percentChange = null,
  onInfoIconClick,
}: PriceInputProps) {
  return (
    <FormControl sx={{ background: colors.background.white, width: "100%", flex: 1 }}>
      <InputLabel
        htmlFor={id}
        shrink
        sx={{
          fontSize: "11px",
          color: colors.text.secondary,
          transform: "translate(14px, -9px) scale(1)",
          "&.Mui-focused": {
            color: colors.text.secondary,
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(1)",
          },
        }}
      >
        {label}
      </InputLabel>
      <OutlinedInput
        type="number"
        id={id}
        startAdornment={
          <InputAdornment position="start">
            <Typography
              sx={{
                fontSize: "13px",
                color: colors.text.secondary,
                opacity: 0.5,
              }}
            >
              $
            </Typography>
          </InputAdornment>
        }
        endAdornment={
          label === "Price" ? (
            <InputAdornment position="end">
              {percentChange !== null && (
                <Tooltip title={`The BuyBox Price is ${percentChange.toFixed(2)}% ${percentChange > 0 ? "higher" : "lower"} than the 90-day average price`}>
                <Typography
                  sx={{
                    color: percentChange > 0 ? colors.success.positive : colors.error.negative,
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.25,
                    mr: 0.5,
                  }}
                >
                  <SvgIcon
                    component={
                      percentChange > 0 ? NorthEastIcon : SouthEastIcon
                    }
                    sx={{ fontSize: "13px" }}
                  />
                  {`${percentChange.toFixed(2)}%`}
                </Typography>
                </Tooltip>
              )}
              <Tooltip title="View different price options with percentage-based adjustments">
                <SvgIcon
                  component={InfoOutlinedIcon}
                  onClick={onInfoIconClick}
                  sx={{
                    fontSize: "18px",
                    color: colors.text.secondary,
                    cursor: "pointer",
                    "&:hover": {
                      color: colors.accent.orange,
                    },
                  }}
                />
              </Tooltip>
            </InputAdornment>
          ) : null
        }
        label={label}
        value={value}
        sx={{
          height: 40,
          borderRadius: "6px",
          backgroundColor: colors.background.white,
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.border.light,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primary.main,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primary.main,
            borderWidth: "1px",
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 2px ${colors.primary.light}`,
          },
          "&.Mui-active .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primary.main,
          },
          "& .MuiOutlinedInput-input": {
            color: colors.text.primary,
            fontSize: "13px",
            fontWeight: "500",
            padding: "10px 14px",
            "&::-webkit-outer-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
            "&::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
            MozAppearance: "textfield",
          },
        }}
        onChange={onChange}
        inputProps={{
          min: 0,
          onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select(),
        }}
        onKeyDown={(e) => {
          if (e.key === "," || e.key === "e") {
            e.preventDefault();
          }
        }}
      />
    </FormControl>
  );
}
